import React, { useState } from "react";
import Client from "../../models/client";
import useSWR from "swr";
import EnduserEventForm, { EnduserEvent, recordTypeLabel } from "../components/EnduserEventForm";
import { deleteEnduserEvent, fetchEnduserEvents } from "../api";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { ActionIcon, Anchor, Box, Button, Center, Group, Loader, Table, Text, ThemeIcon, Title, Tooltip } from "@mantine/core";
import { BellIcon, PencilSimpleIcon, PlusIcon, TrashIcon } from "@phosphor-icons/react";
import { confirmDelete } from "../utils/confirm";

interface Props {
  client: Client
}

const EnduserEventsContainer: React.FC<Props> = ({ client }) => {
  const { data: events, mutate } = useSWR<EnduserEvent[]>(
    `/admin/clients/${client.id}/enduser_events`,
    () => fetchEnduserEvents(client.id),
  )

  const [opened, { open, close }] = useDisclosure(false)
  const [targetEvent, setTargetEvent] = useState<EnduserEvent | null>(null)

  const handleCreate = () => {
    setTargetEvent(null)
    open()
  }

  const handleEdit = (item: EnduserEvent) => {
    setTargetEvent(item)
    open()
  }

  const handleDelete = async (item: EnduserEvent) => {
    if (!confirmDelete(`イベント（ID: ${item.id}）`)) {
      return
    }
    try {
      await deleteEnduserEvent(client.id, item.id)
      await mutate()
      notifications.show({ message: 'イベントを削除しました', color: 'teal' })
    } catch (e) {
      console.error('イベントの削除に失敗しました', e)
      notifications.show({
        message: 'このイベントには通知設定が紐づいているため削除できません。先に通知設定を削除してください',
        color: 'red',
      })
    }
  }

  if (!events) {
    return (
      <Center mih={200}>
        <Loader />
      </Center>
    )
  }

  return (
    <Box>
      <Group gap={12} mb={16}>
        <ThemeIcon variant="transparent" color="#4BC1C7">
          <BellIcon weight="fill" size={36} />
        </ThemeIcon>
        <Title size={24} fw={600} c="#000000CC">
          イベント設定
        </Title>
      </Group>

      <Text fz={14} c="#000000CC" mb={30}>
        イベント設定では、どの成果ポイントで紹介者に通知を送るかを設定します。<br />
        ※イベント設定の方法については、
        <Anchor
          href="https://www.creativehope.co.jp/invyknowledge/notification/01"
          target="_blank"
          rel="noopener noreferrer"
          fz={14}
          underline='always'
        >
          マニュアル（invyご利用ガイド）
        </Anchor>
        をご確認ください。<br />
        ※成果ポイントが複数ある場合は、それぞれに対して設定することが可能です。
      </Text>

      <Group justify="space-between" mb={16}>
        <Button
          variant="filled"
          radius="xl"
          onClick={handleCreate}
          leftSection={<PlusIcon size={16} weight="bold" />}
        >
          新規作成
        </Button>
      </Group>

      <Box
        style={{
          border: '1px solid #0000001A',
          borderRadius: '8px',
          overflow: 'hidden',
          width: '100%',
        }}
      >
        <Table
          withColumnBorders
          styles={{
            th: { backgroundColor: '#00000008' },
          }}
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={80}>ID</Table.Th>
              <Table.Th w="35%">イベント名</Table.Th>
              <Table.Th w="35%">タイプ</Table.Th>
              <Table.Th w={80} ta="center">編集</Table.Th>
              <Table.Th w={80} ta="center">削除</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {events.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={5}>
                  <Text c="#00000099" ta="center" py="md">
                    イベントが登録されていません
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              events.map((item) => (
                <Table.Tr key={item.id}>
                  <Table.Td>{item.id}</Table.Td>
                  <Table.Td>{item.name}</Table.Td>
                  <Table.Td>{recordTypeLabel(item.record_type)}</Table.Td>
                  <Table.Td ta="center">
                    <Tooltip label="編集">
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        onClick={() => handleEdit(item)}
                      >
                        <PencilSimpleIcon size={16} />
                      </ActionIcon>
                    </Tooltip>
                  </Table.Td>
                  <Table.Td ta="center">
                    <Tooltip label="削除">
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        onClick={() => handleDelete(item)}
                      >
                        <TrashIcon size={16} />
                      </ActionIcon>
                    </Tooltip>
                  </Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      </Box>

      <EnduserEventForm
        opened={opened}
        onClose={close}
        event={targetEvent}
        client={client}
        onSaved={mutate}
      />
    </Box>
  )
}

export default EnduserEventsContainer
